import { useState, useEffect } from 'react';
import { Bell, AlertTriangle, CheckCircle2, ChevronDown } from 'lucide-react';

interface CalculationResult {
    amax: number;
    an: number;
    count: number;
    steps: string[];
}

export default function DetectionCalculator() {
    const [surface, setSurface] = useState<number>(0);
    const [height, setHeight] = useState<number>(3);
    const [riskType, setRiskType] = useState<string>('generic');
    const [result, setResult] = useState<CalculationResult | null>(null);

    // K factors from Rule R7 Table 3.2
    const riskTypes = [
        { id: 'generic', label: 'General / Offices / Circulation', k: 1.0 },
        { id: 'technical', label: 'Technical / Server Rooms', k: 0.3 },
        { id: 'hotel', label: 'Hotel Room', k: 0.6 },
        { id: 'storage_paper', label: 'Storage (Paper/Cardboard)', k: 0.6 },
        { id: 'archive', label: 'Archives', k: 1.0 },
    ];

    const calculate = () => {
        if (surface <= 0) return;

        const selectedRisk = riskTypes.find(r => r.id === riskType) || riskTypes[0];
        const k = selectedRisk.k;
        const steps: string[] = [];

        // 1. Determine Amax (Table 3.1)
        // Simplifying for standard smoke detectors (slope <= 20)
        let amax = 80;
        let limitReason = "Standard (<80m², h<=12m)";

        if (surface > 80) {
            if (height <= 6) {
                amax = 60;
                limitReason = "Large Area (>80m²), Standard Height";
            } else if (height <= 12) {
                amax = 80;
                limitReason = "Large Area (>80m²), High Ceiling";
            } else {
                // Height > 12m
                steps.push("⚠️ Height > 12m: Point detectors not suitable. Use Beam/Aspiration.");
                amax = 0; // Invalid
            }
        }

        // Check height limits generally
        if (height > 12) {
            amax = 0;
        }

        if (amax === 0) {
            setResult({ amax: 0, an: 0, count: 0, steps });
            return;
        }

        steps.push(`1. Amax Determination: ${amax}m² (${limitReason})`);
        steps.push(`2. Risk Factor (K): ${k} (${selectedRisk.label})`);

        // 2. Calculate An
        const an = k * amax;
        steps.push(`3. Nominal Surface (An): ${k} * ${amax} = ${an.toFixed(1)}m²`);

        // 3. Calculate Count
        const rawCount = surface / an;
        const count = Math.ceil(rawCount);
        steps.push(`4. Detector Count: ${surface} / ${an.toFixed(1)} = ${rawCount.toFixed(2)} -> ${count}`);

        setResult({ amax, an, count, steps });
    };

    useEffect(() => {
        calculate();
    }, [surface, height, riskType]);

    return (
        <div className="card-terminal p-6 animate-fadeIn">
            <div className="flex items-center gap-3 mb-6 border-b border-[var(--blueprint-line)] pb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <Bell className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                    <h2 className="text-xl font-display font-bold text-white">Fire Detection</h2>
                    <p className="text-sm text-gray-400">Rule R7 (APSAD)</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Inputs */}
                <div className="space-y-6">
                    <div>
                        <label className="label-terminal">Room Surface (m²)</label>
                        <input
                            type="number"
                            value={surface}
                            onChange={(e) => setSurface(Math.max(0, Number(e.target.value)))}
                            className="input-terminal"
                            placeholder="e.g. 50"
                        />
                    </div>

                    <div>
                        <label className="label-terminal">Ceiling Height (m)</label>
                        <input
                            type="number"
                            value={height}
                            onChange={(e) => setHeight(Math.max(0, Number(e.target.value)))}
                            className="input-terminal animate-pulse-focus"
                            placeholder="e.g. 3"
                        />
                        {height > 12 && (
                            <div className="mt-2 text-xs text-red-400 flex items-center gap-1 bg-red-500/10 p-2 rounded">
                                <AlertTriangle className="w-3 h-3" />
                                Height exceeds 12m limit for point detectors!
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="label-terminal">Space Type (K Factor)</label>
                        <div className="relative">
                            <select
                                value={riskType}
                                onChange={(e) => setRiskType(e.target.value)}
                                className="input-terminal appearance-none cursor-pointer"
                            >
                                {riskTypes.map(t => (
                                    <option key={t.id} value={t.id} className="bg-gray-900">
                                        {t.label} (K={t.k})
                                    </option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-4 top-3.5 w-4 h-4 text-gray-500 pointer-events-none" />
                        </div>
                    </div>
                </div>

                {/* Results */}
                <div className="bg-black/40 rounded-xl p-6 border border-[var(--blueprint-line)] relative overflow-hidden flex flex-col justify-between min-h-[300px]">
                    {/* Background Icon */}
                    <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                        <Bell className="w-32 h-32" />
                    </div>

                    {result && result.count > 0 ? (
                        <>
                            <div className="relative z-10">
                                <h3 className="text-lg font-semibold text-white mb-6">Coverage Analysis</h3>

                                <div className="grid grid-cols-2 gap-4 mb-6">
                                    <div className="p-3 bg-white/5 rounded-lg border border-[var(--blueprint-line-light)]">
                                        <div className="text-xs text-gray-500 mb-1">Max Area/Detector</div>
                                        <div className="text-xl font-mono text-blue-400">{result.an.toFixed(1)} m²</div>
                                    </div>
                                    <div className="p-3 bg-white/5 rounded-lg border border-[var(--blueprint-line-light)]">
                                        <div className="text-xs text-gray-500 mb-1">Density</div>
                                        <div className="text-xl font-mono text-gray-300">{(surface / result.count).toFixed(1)} m²/unit</div>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <div className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-2">Algorithm Trace</div>
                                    {result.steps.map((step, idx) => (
                                        <div key={idx} className="text-sm text-gray-400 font-mono pl-2 border-l border-blue-500/30">
                                            {step}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="mt-6 pt-4 border-t border-[var(--blueprint-line)] relative z-10">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-300">Minimum Detectors</span>
                                    <div className="flex items-center gap-3">
                                        <span className="text-4xl font-bold text-blue-500">{result.count}</span>
                                        <CheckCircle2 className="w-6 h-6 text-green-500" />
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center text-gray-500 relative z-10">
                            {height > 12 ? (
                                <div className="text-center text-red-400">
                                    <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                    <p>System Limit Exceeded</p>
                                    <p className="text-sm mt-2">See recommendations for &gt;12m</p>
                                </div>
                            ) : (
                                <>
                                    <Bell className="w-12 h-12 mb-3 opacity-20" />
                                    <p>Enter parameters to calculate coverage</p>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
